"use client";

import Link from "next/link";
import { coreServices, type CoreService } from "@/shared/config/coreServices";
import { serviceDetailContentBySlug } from "@/shared/config/serviceDetailContent";
import { NurseServicePage } from "./NurseServicePage";

export function ServiceDetailPage({ service }: { service: CoreService }) {
  if (service.slug === "nurse-services") {
    return <NurseServicePage />;
  }

  const relatedServices = coreServices.filter((item) => item.slug !== service.slug).slice(0, 3);
  const deepDiveContent = serviceDetailContentBySlug[service.slug];
  const featureCards = deepDiveContent?.featureCards ?? [];
  const careJourney = (deepDiveContent?.highlightBullets ?? service.benefits).slice(0, 4).map((title) => ({ title }));

  return (
    <main id={`service-${service.slug}`}>
      <section className="wireframe-service-hero">
        <div className="container wireframe-detail-layout">
          <div className="section-title">
            <p>Carevinco Service</p>
            <h2>
              {service.title} <span>At Home.</span>
            </h2>
            {/* <small>{service.summary}</small> */}
            <div className="button-row" style={{ marginTop: 34 }}>
              <button className="btn btn--primary" type="button" data-open-inquiry>
                Book a Care Visit
              </button>
              <Link className="btn btn--outline" href="/services">
                View All Services
              </Link>
            </div>
          </div>
          <article className="wireframe-detail-panel">
            <h3>Service Snapshot</h3>
            <ul>
              {(featureCards.length ? featureCards : service.benefits.map((benefit) => ({ title: benefit }))).slice(0, 5).map((item) => (
                <li key={item.title}>{item.title}</li>
              ))}
            </ul>
          </article>
        </div>
      </section>

      <section className="proof section-pad">
        <div className="container">
          <div className="section-title section-title--center">
            <p>What’s Included</p>
            <h2>
              Practical Care Support With <span>Clear Coordination.</span>
            </h2>
            <small>Each service is structured to keep families informed and care delivery consistent.</small>
          </div>
          <div className="wireframe-service-grid">
            {service.offerings.slice(0, 8).map((offering, index) => (
              <article className="wireframe-service-card" key={offering}>
                <b>{String(index + 1).padStart(2, "0")}</b>
                <h3>{offering}</h3>
                <p>{service.benefits[index % service.benefits.length] || service.summary}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {careJourney.length ? (
        <section className="steps-card" aria-label={`${service.title} care journey`}>
          <div className="steps-card__label">
            <span>Care Journey in </span>
            <strong>{careJourney.length}</strong>
            <span> Simple Steps</span>
          </div>
          <div className="steps-card__grid">
            {careJourney.map((item, index) => (
              <article className={`step-item ${["step-blue", "step-orange", "step-green", "step-purple"][index % 4]}`} key={item.title}>
                <b>{String(index + 1).padStart(2, "0")}</b>
                <div>
                  <small>Step {String(index + 1).padStart(2, "0")}</small>
                  <p>{item.title}</p>
                </div>
              </article>
            ))}
          </div>
        </section>
      ) : null}

      <section className="standards section-pad">
        <div className="container">
          <div className="section-title section-title--center">
            <p>Related Care</p>
            <h2>
              Explore More <span>Carevinco Services.</span>
            </h2>
          </div>
          <div className="wireframe-service-grid">
            {relatedServices.map((item, index) => (
              <article className="wireframe-service-card" key={item.slug}>
                <b>{String(index + 1).padStart(2, "0")}</b>
                <h3>{item.title}</h3>
                <p>{item.summary}</p>
                <Link className="text-link" href={`/services/${item.slug}`}>
                  Explore Service →
                </Link>
              </article>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
